import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from '../utils/toast';

// Real-time attendance data
export const useRealtimeAttendance = (filters = {}, options = {}) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['attendance', 'realtime', filters],
    queryFn: async () => {
      const response = await staffAPI.getRealtimeAttendance(filters);
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - real-time data needs updates but not every 30s
    refetchInterval: user ? 5 * 60 * 1000 : false, // Only poll when authenticated
    refetchIntervalInBackground: false, // Don't refresh when tab not active to reduce server load
    enabled: !!user, // Only fetch when user is authenticated
    placeholderData: (prev) => prev, // Keep previous data visible while filter changes load
    ...options, // Allow overriding options
  });
};

// Daily attendance data
export const useDailyAttendance = (date, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['attendance', 'daily', date, filters],
    queryFn: async () => {
      const response = await staffAPI.getDailyAttendance({ date, ...filters });
      return response.data?.results || response.data || [];
    },
    enabled: !!date,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (prev) => prev, // Keep previous data visible while new search results load
    ...options,
  });
};

// Attendance statistics
export const useAttendanceStatistics = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['attendance', 'statistics', params],
    queryFn: async () => {
      const response = await staffAPI.getAttendanceStatistics(params);
      const data = response.data || {};
      if (data.active_employees === undefined && Array.isArray(data.employees)) {
        data.active_employees = data.employees.filter(e => e.is_active !== false).length;
      }
      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};

// Daily attendance statistics
export const useDailyAttendanceStatistics = (date) => {
  return useQuery({
    queryKey: ['attendance', 'statistics', 'daily', date],
    queryFn: async () => {
      const response = await staffAPI.getDailyStatistics({ date });
      return response.data || {};
    },
    enabled: !!date,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Attendance summary
export const useAttendanceSummary = () => {
  return useQuery({
    queryKey: ['attendance', 'summary'],
    queryFn: async () => {
      const response = await staffAPI.getAttendanceSummary();
      return response.data || {};
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Late arrivals
export const useLateArrivals = () => {
  return useQuery({
    queryKey: ['attendance', 'late-arrivals'],
    queryFn: async () => {
      const response = await staffAPI.getLateArrivals();
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Project monitoring data
export const useProjectMonitoring = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['attendance', 'projects', 'monitoring', params],
    queryFn: async () => {
      const response = await staffAPI.getProjectMonitoring(params);
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // Explicitly disable - prevent refetch on tab focus
    refetchOnMount: false, // Only refetch if query key changed (user changed filters)
    refetchInterval: false, // No automatic polling
    structuralSharing: true, // Enable request deduplication
    placeholderData: (prev) => prev,
    ...options,
  });
};

// Exceptions & alerts data
export const useExceptionsAlerts = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['attendance', 'exceptions', params],
    queryFn: async () => {
      // Always fetch all pages to get complete results (not just when department filter is applied)
      // Progressive loading: Fetch first page, then remaining pages in parallel
      const MAX_PAGES = 100; // Increased safety limit
      const PAGE_SIZE = 1000; // Larger page size for efficiency
      
      try {
        // Fetch first page to get total count and show immediate results
        const firstPageResponse = await staffAPI.getExceptionsAlerts({ 
          ...params, 
          page: 1, 
          page_size: PAGE_SIZE 
        });
        const firstPageData = firstPageResponse.data || {};
        const firstPageExceptions = firstPageData.exceptions || [];
        const totalPages = firstPageData.pagination?.total_pages || 1;
        
        // If only one page, return immediately
        if (totalPages <= 1) {
          return {
            exceptions: firstPageExceptions,
            summary: firstPageData.summary || {},
            pagination: firstPageData.pagination || {},
          };
        }
        
        // Safety check: don't fetch more than MAX_PAGES
        const pagesToFetch = Math.min(totalPages - 1, MAX_PAGES - 1);
        
        // Fetch remaining pages in parallel with error handling
        const remainingPages = Array.from({ length: pagesToFetch }, (_, i) => i + 2);
        const remainingPromises = remainingPages.map(page =>
          staffAPI.getExceptionsAlerts({ 
            ...params, 
            page, 
            page_size: PAGE_SIZE 
          })
            .then(response => ({
              success: true,
              page,
              data: response.data || {},
            }))
            .catch(error => {
              console.error(`Failed to fetch exceptions page ${page}:`, error);
              return {
                success: false,
                page,
                data: { exceptions: [], pagination: {} },
              };
            })
        );
        
        // Wait for all remaining pages (with timeout protection)
        const remainingResults = await Promise.all(remainingPromises);
        
        // Combine all exceptions
        const allExceptions = [
          ...firstPageExceptions,
          ...remainingResults.flatMap(result => result.data.exceptions || [])
        ];
        
        // Log any failures
        const failedPages = remainingResults.filter(r => !r.success);
        if (failedPages.length > 0) {
          console.warn(`Failed to fetch ${failedPages.length} page(s) of exceptions`);
        }
        
        return {
          exceptions: allExceptions,
          summary: firstPageData.summary || {},
          pagination: {
            ...firstPageData.pagination,
            total_items: allExceptions.length,
            total_pages: 1,
            has_next: false,
            has_previous: false,
          },
        };
      } catch (error) {
        console.error('Error fetching exceptions:', error);
        // Return empty result on error rather than crashing
        return {
          exceptions: [],
          summary: {},
          pagination: {},
        };
      }
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
    ...options, // Allow overriding options (e.g., enabled)
  });
};

// Lightweight pending exceptions count (always enabled, for badges)
export const useExceptionsPendingCount = () => {
  return useQuery({
    queryKey: ['attendance', 'exceptions', 'pendingCount'],
    queryFn: async () => {
      const response = await staffAPI.getExceptionsPendingCount();
      return response.data?.count ?? 0;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

// All clock events (admin) for a date or range
export const useClockEvents = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['attendance', 'clock-events', params],
    queryFn: async () => {
      // Use clock-events endpoint; pass either a single date or a date range (not both)
      const { date, start_date, end_date, page_size } = params || {};
      if (date) {
        const response = await staffAPI.getClockEvents({ date, page_size });
        return response.data?.results || response.data || [];
      }
      if (start_date || end_date) {
        const response = await staffAPI.getClockEvents({ start_date, end_date, page_size });
        return response.data?.results || response.data || [];
      }
      const response = await staffAPI.getClockEvents({ page_size });
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
    ...options,
  });
};

// Attendance mutations
export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.updateAttendance(id, data),
    onSuccess: () => {
      // Invalidate all attendance caches
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
      toast.success('Attendance updated successfully');
    },
    onError: (error) => {
      console.error('Error updating attendance:', error);
      toast.error('Failed to update attendance');
    },
  });
};

export const useBulkUpdateAttendance = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => staffAPI.bulkUpdateAttendance(data),
    onSuccess: () => {
      // Invalidate all attendance caches
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
      toast.success('Bulk attendance update completed');
    },
    onError: (error) => {
      console.error('Error bulk updating attendance:', error);
      toast.error('Failed to update attendance records');
    },
  });
};
